/**
 * A person's display name (users.display_name): the name reports and comments carry, which the person sets under
 * Settings, Security and privacy (PATCH /v1/platform/me, platform-core/internal/orgs/display_name.go). The rules the
 * server keeps, said again here so a mistake shows before a save, and the words for what went wrong. Pure.
 */
import { ApiClientError } from "@/lib/api-client";

/** The longest name the platform keeps, in characters, counted after whitespace is collapsed. */
export const MAX_DISPLAY_NAME = 100;

/**
 * True when s is a name to show for a person: something besides spaces, and not an address. The platform's display
 * name starts as the person's email, which is not a name (Foundry Scope will not sign a report with one).
 */
export function isPersonName(s: string | null | undefined): s is string {
  return typeof s === "string" && s.trim() !== "" && !s.includes("@");
}

/** The name as the platform keeps it: whitespace collapsed to one space, trimmed. */
export function normalizeDisplayName(raw: string): string {
  return raw.split(/\s+/u).filter(Boolean).join(" ");
}

/** True when s holds a control character, as the server reads one (Go's unicode.IsControl: U+0000 to U+001F, U+007F to
 *  U+009F). A tab and a line break are two. */
function hasControl(s: string): boolean {
  for (const ch of s) {
    const c = ch.codePointAt(0) ?? 0;
    if (c <= 0x1f || (c >= 0x7f && c <= 0x9f)) return true;
  }
  return false;
}

/** The fillers that render blank, though they are not format characters: the Hangul fillers and the blank Braille
 *  pattern. */
const BLANK_FILLERS = new Set([0x115f, 0x1160, 0x3164, 0xffa0, 0x2800]);

/** True when s holds a character a name may not, as the server reads one (auth.IsInvisibleInName): a format character
 *  (Unicode category Cf: zero widths, the byte order mark, the soft hyphen, direction marks, overrides and isolates)
 *  other than the zero width non-joiner and joiner (U+200C, U+200D, which Indic and Persian scripts need), or a blank
 *  filler. */
function hasInvisible(s: string): boolean {
  for (const ch of s) {
    const c = ch.codePointAt(0) ?? 0;
    if (c === 0x200c || c === 0x200d) continue;
    if (BLANK_FILLERS.has(c) || /\p{Cf}/u.test(ch)) return true;
  }
  return false;
}

/** True when s holds a letter or a digit, in any script (Go's unicode.IsLetter or unicode.IsNumber). */
function hasLetterOrDigit(s: string): boolean {
  return /[\p{L}\p{N}]/u.test(s);
}

/** What is wrong with a name before it is sent, in plain words, or null when the platform will take it. */
export function displayNameProblem(raw: string): string | null {
  if (hasControl(raw)) return "Use one line of plain text, without tabs or line breaks.";
  if (hasInvisible(raw)) return "Use only characters that show: no invisible spaces or direction marks.";
  const name = normalizeDisplayName(raw);
  if (name === "") return "Type your name.";
  if ([...name].length > MAX_DISPLAY_NAME) return `Keep it to ${MAX_DISPLAY_NAME} characters or fewer.`;
  if (name.includes("@")) return "Use your name, not an email address.";
  if (!hasLetterOrDigit(name)) return "Use at least one letter or number.";
  return null;
}

/** What a failed save says, in plain words. sent is the name that was sent, to say what the server refused in it. */
export function displayNameSaveError(err: unknown, sent: string): string {
  const code =
    err instanceof ApiClientError ? (err.body as { error?: string } | null)?.error : undefined;
  if (code === "invalid_display_name") {
    return displayNameProblem(sent) ?? "That name cannot be used. Use 1 to 100 characters of plain text, without an @.";
  }
  if (code === "account_disabled") return "Your account is switched off, so your name cannot be changed.";
  if (code === "user_not_found") return "Your account is not set up yet. Sign out, sign in again, and try once more.";
  if (err instanceof ApiClientError && err.status === 401) return "You are signed out. Sign in again and try once more.";
  return "Your name could not be saved. Please try again.";
}
